<template>
  <section
    class="my-8 md:mb-8 mx-auto bg-black pt-6 px-2 md:px-10 pb-0 shadow-black shadow-xl divide-y-2 divide-white font-helvetica text-[22px] text-[#999999] max-w-[1400px]"
  >
    <RallyLogoDownload />
    <div class="pt-8 mt-8">
      <h1 class="text-white text-2xl md:text-3xl font-russo">
        ROAD AND STAGE RALLY ROADBOOKS
      </h1>
      <h2 class="font-semibold">FIA Legal Roadbooks Direct to PDF or Digital</h2>
      <p class="font-arial text-[16px] md:text-[18px] font-semibold pt-2">
        Rally Navigator is the complete tool to create a road rally roadbook.
        Design your route on the map, capture distances and headings, draw tulip
        diagrams, and export a print-ready rally roadbook PDF for any road book
        rally or stage event. New to roadbooks? Start with our
        <router-link to="/rally-roadbook" class="text-[#FF0000] hover:underline"
          >rally roadbook overview</router-link
        >, then follow the step-by-step tutorial below.
      </p>
      <GuideDownloadCta />
      <EventsBanner :images="eventsBannerImages" />
    </div>
    <div class="py-8 mt-8 flex flex-col gap-4 text-xl md:text-2xl">
      <YouTubeEmbed
        style="border: 4px solid #999999"
        class="m-auto w-[300px] h-[182px] md:w-[650px] md:h-[395px] lg:w-[896px] lg:h-[448px]"
        title="Rally Navigator Road and Stage Rally video playlist"
        src="https://www.youtube.com/embed/videoseries?list=PLQY2A90JJtFI"
        thumb="https://i.ytimg.com/vi/NCGJn14UefM/maxresdefault.jpg"
      />
      <h2 class="font-semibold">Powered by MapBox and GPS</h2>
      <h2 class="font-semibold">
        Matching GPX Tracks with Enhanced Route and Scoring Data
      </h2>
      <h2 class="font-semibold">
        Pro Users Can Upload Custom Navigation Icons and Custom Roadbook Header
        Images
      </h2>
      <picture class="contents">
        <source srcset="/images/create-road-stage/Road-Rally-Roadbook-1.webp" type="image/webp" />
        <img
        decoding="async"
        fetchpriority="high"
        width="587"
        height="939"
        src="/images/create-road-stage/Road-Rally-Roadbook-1.png"
        class="flex m-auto"
        alt="Sample road rally roadbook page created in Rally Navigator"
        />
      </picture>
    </div>
    <div class="pt-8 mt-8 flex flex-col gap-4" @click="onSampleClick">
      <h2 class="font-semibold">
        Rally Navigator Export and Print Sample Files
      </h2>
      <SampleRoadbookIntro
        name="Road &amp; Stage Rally"
        stats="25 km · 12 waypoints · 6-page A5 roadbook"
        description="A special stage up scenic Mt Rose Hwy from Reno through Galena Forest to the 8,900 ft summit, then a transit to a Lake Tahoe overlook — with time controls, section headers and average speeds."
        image="road-rally-roadbook-page"
        :image-width="574"
        :image-height="578"
        pdf-href="/create-road-rally/Road_Rally_Sample-A5.pdf"
        rn2-href="/create-road-rally/Road_Rally_Sample.rn2"
      />
      <h2 class="font-bold text-left text-[17px]">
        RN2 File &#8211; Road Rally Sample Route
      </h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] text-[#A9A9A9]"
      >
        <li>
          <p class="text-[#FF0000] hover:underline font-bold">
            <a
              href="create-road-rally/Road_Rally_Sample.rn2"
              download="Road_Rally_Sample.rn2"
              target="_blank"
            >
              RN2 Road Rally Sample
            </a>
          </p>
        </li>
      </ul>
      <h2 class="font-bold text-left text-[17px]">
        GPX, KML and KMZ Files &#8211; Road Rally Sample Route
      </h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] text-[#A9A9A9]"
      >
        <li>
          <p class="text-[#FF0000] hover:underline font-bold">
            <a href="create-road-rally/Road_Rally_Sample.gpx" target="_blank">
              GPX Route File Including OpenRally Scoring and Control Data
            </a>
          </p>
        </li>
        <li>
          <p class="text-[#FF0000] hover:underline font-bold">
            <a
              href="create-road-rally/Road_Rally_Sample-Route.gpx"
              target="_blank"
            >
              GPX Route File &#8211; Waypoints Only for GPS Devices and Mapping
              Tools
            </a>
          </p>
        </li>
        <li>
          <p class="text-[#FF0000] hover:underline font-bold">
            <a href="create-road-rally/Road_Rally_Sample.kml" target="_blank">
              KML Road Rally
            </a>
          </p>
        </li>
        <li>
          <p class="text-[#FF0000] hover:underline font-bold">
            <a href="create-road-rally/Road_Rally_Sample.kmz" target="_blank">
              KMZ Road Rally &#8211; Waypoints Grouped by Control Type
            </a>
          </p>
        </li>
      </ul>
      <h2 class="font-bold text-left text-[17px]">
        PDF File &#8211; Road Rally Roadbook
      </h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] text-[#A9A9A9]"
      >
        <li>
          <p class="text-[#FF0000] hover:underline font-bold">
            <a
              href="create-road-rally/Road_Rally_Sample-A4.pdf"
              target="_blank"
            >
              A4 &#8211; Road Rally Roadbook
            </a>
          </p>
        </li>
        <li>
          <p class="text-[#FF0000] hover:underline font-bold">
            <a
              href="create-road-rally/Road_Rally_Sample-A5.pdf"
              target="_blank"
            >
              A5 &#8211; Road Rally Roadbook
            </a>
          </p>
        </li>
        <li>
          <p class="text-[#FF0000] hover:underline font-bold">
            <a
              href="create-road-rally/Road_Rally_Sample-Letter.pdf"
              target="_blank"
            >
              Letter &#8211; Road Rally Roadbook
            </a>
          </p>
        </li>
        <li>
          <p class="text-[#FF0000] hover:underline font-bold">
            <a
              href="create-road-rally/Road_Rally_Sample-Legal.pdf"
              target="_blank"
            >
              Legal &#8211; Road Rally Roadbook
            </a>
          </p>
        </li>
      </ul>
      <h2 class="font-bold text-left text-[17px]">
        Time Speed Distance Worksheet
      </h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] text-[#A9A9A9]"
      >
        <li>
          <p class="text-[#FF0000] hover:underline font-bold">
            <a
              href="create-road-rally/Road_Rally_Sample-TSD.xlsx"
              target="_blank"
            >
              Time Speed Distance Sheet &#8211; Road Rally Sample (XLSX)
            </a>
          </p>
        </li>
        <li>
          <p class="text-[#FF0000] hover:underline font-bold">
            <router-link to="/time-speed-distance">More Info Here</router-link>
          </p>
        </li>
      </ul>
      <h2 class="font-bold text-left text-[17px]">FIA Stage Rally</h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] text-[#A9A9A9]"
      >
        <li>
          <p class="text-[#FF0000] hover:underline font-bold">
            <a
              href="https://www.fia.com/system/files/documents/wrc_2026_sr_version_13_january_2026.pdf"
              target="_blank"
              rel="noopener"
            >
              FIA WRC Road Rally Rules and Roadbook Specifications
            </a>
          </p>
        </li>
      </ul>
      <router-link to="/timing-scoring">
        <h2 class="text-[#FF0000] font-bold hover:underline">
          RALLY NAVIGATOR INTEGRATES WITH LEADING RALLY TRACKING SYSTEMS
        </h2>
      </router-link>
    </div>
    <div class="py-8 mt-8 flex flex-col gap-4">
      <h2 class="font-semibold">Create a Roadbook by Drawing on the Map</h2>
      <p class="font-arial text-[14px] font-bold text-left">
        Start with File &gt; New Road / Stage Rally. Already built it as Cross
        Country? Roadbook &gt; Change Roadbook Format converts it to Road /
        Stage Rally.
      </p>
      <h2 class="font-semibold text-left">
        Double Click the Map &amp; Create Your First Waypoint
      </h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] font-bold"
      >
        <li>
          Enter Route Name, Description, Start and Finish Location in Header
        </li>
        <li>Click along route on Map to create Track Handles and red track.</li>
        <li>
          Double Click Track handle again to create all Waypoints along Route to
          Finish
        </li>
        <li>
          Carefully place 1st Track Handle after Waypoint to set direction of
          path leaving Waypoint, it&#8217;s used to automatically draw simple
          Tulip
        </li>
      </ul>
      <h2 class="font-semibold text-left">
        Mapbox &#8211; Snap to Roads &#8211; See the Power of Rally Navigator
      </h2>
      <ul
        class="pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] font-bold"
      >
        <li class="list-disc">
          Snap to Roads. Rally Navigator will quickly trace roads shown on the
          Map
        </li>
        <li>
          <ul
            class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] font-bold"
          >
            <li>PC: Ctrl + Click Road on Map</li>
            <li>Mac: Cmd + Click Road on Map</li>
          </ul>
        </li>
        <li class="list-disc">
          Snap to Roads with Text Navigation Added to Each Waypoint
        </li>
        <li>
          <ul
            class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] font-bold"
          >
            <li>PC: Ctrl + Alt + Click Road on Map</li>
            <li>Mac: Cmd + Option + Click Road on Map</li>
          </ul>
        </li>
        <li class="list-disc">
          Or turn on Map &gt; Follow Roads and every click follows the road.
          Follow Roads works mid-route too.
        </li>
      </ul>
      <picture class="contents">
        <source srcset="/images/create-road-stage/RR-Screenshot-1.webp" type="image/webp" />
        <img
          decoding="async"
          width="1919"
          height="1029"
          src="/images/create-road-stage/RR-Screenshot-1.png"
          class="flex m-auto"
          alt="Road rally route drawn on the Rally Navigator map with snap-to-road"
        />
      </picture>
      <h2 class="font-semibold">Create a Roadbook from GPX, KML or KMZ Tracks</h2>
      <h3 class="font-arial text-[16px] font-bold text-white">
        Import GPX, KML or KMZ tracks. For best results, use a single continuous
        path. RiverNotes 2 and Tulip roadbooks import too.
      </h3>
      <h2 class="font-semibold text-left">
        Import a GPX, KML or KMZ Track to Get Started
      </h2>
      <ul
        class="list-disc pl-8 text-left pb-2 flex flex-col gap-2 font-arial text-[14px] font-bold"
      >
        <li>
          Select File &gt; Import &gt; Import GPX/KML/KMZ File. Choose Road &amp;
          Stage Rally as the roadbook format, then pick the import options:
          Remove Timestamps, Remove Extra Track Points, and Include Waypoints to
          turn the file&#8217;s waypoints into roadbook waypoints.
        </li>
        <li>You will see imported Tracks projected onto Main Map Screen.</li>
        <li>
          Big GPS recordings are simplified automatically on import, so the app
          stays fast.
        </li>
        <li>
          You can Insert Additional Tracks at the Start or End of the Track
          using File, Insert Track
        </li>
        <li>
          Enter Route Name, Description, Start and Finish Location in Header
        </li>
      </ul>
      <picture class="contents">
        <source srcset="/images/create-road-stage/RR-Screenshot-2.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="1919"
          height="1029"
          src="/images/create-road-stage/RR-Screenshot-2.png"
          class="flex m-auto"
          alt="Road rally roadbook waypoints and tulip diagrams in Rally Navigator"
        />
      </picture>
      <h2 class="font-semibold">
        Fine Tune Track and Create Waypoints Along the Route
      </h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] font-bold"
      >
        <li>
          Mouse over the red Track and Click to create a track handle. Place
          handle carefully along track on Map to align track along your intended
          route. The Track is used to measure distance and must be accurate.
        </li>
        <li>
          Double Click a Track Handle to create a Waypoint at each intersection
          or POI along the route. Rally Navigator will automatically capture
          segment and total distance, generate tulip, GPS coordinates.
        </li>
        <li>
          Select the Road Type of the Main Tulip Arrow by clicking on Tulip road
          and then clicking on desired road type in the drawing tools bar.
          Changing a road type with change subsequent tulips also.
        </li>
      </ul>
      <h2 class="font-semibold">Add Waypoint Details: Roads, Icons Text</h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] font-bold"
      >
        <li>Select a Waypoint by clicking in Roadbook.</li>
        <li>
          Drag desired &#8220;no go&#8221; road segments into Waypoint editor
          and shape road to create tulip, so curves in road and angles of roads
          at intersection exactly match.
        </li>
        <li>
          Drag and drop proper Navigation icons and signs into Tulip and Text
          Box. Rally Navigator utilizes full library of FIA Navigational Icons.
          Pro Users can add Custom Icons.
        </li>
        <li>
          Right Click in a box to Add Text Descriptions with turn info and
          navigational hints to each Waypoint.
        </li>
        <li>Right Click in Text box to Add a Background Image</li>
        <li>
          Continue along Route till you have fine tuned Track and created
          details for each Waypoint.
        </li>
      </ul>
      <h2 class="font-semibold">Time Controls</h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] font-bold"
      >
        <li>
          Drop a Time Control icon and open its properties. Type target times as
          hours and minutes (h:mm).
        </li>
        <li>
          Each control prints its own number (TC1A, TC1B&#8230;), and special
          stages show Time Allowed and Average Speed.
        </li>
        <li>
          Set Roadbook &gt; Decimal Format to 0,0, 0,00 or 0,000. Turn on Show
          Alternate Distance Units to print km and miles side by side. Partial
          distances always add up to the printed totals.
        </li>
      </ul>
      <h2 class="font-semibold">Print and Export</h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] font-bold"
      >
        <li>
          Choose File &gt; Print to PDF for A5, A4, Letter or Legal.
        </li>
        <li>
          Use File &gt; Export for OpenRally GPX, Plain GPX, KML or the TSD
          worksheet. Road Rally roadbooks also export to TerraPirata for digital
          roadbooks and tracking.
        </li>
        <li>
          File &gt; Export &gt; Export To Rally Device has ready-made profiles for
          Stella, RallySafe, Sporttrax, YB Tracking and more.
          <router-link to="/timing-scoring" class="text-[#FF0000] hover:underline"
            >See all integrations</router-link
          >
        </li>
      </ul>
      <picture class="contents">
        <source srcset="/images/create-road-stage/RR-Screenshot-3.webp" type="image/webp" />
        <img
          decoding="async"
          loading="lazy"
          width="1917"
          height="1029"
          src="/images/create-road-stage/RR-Screenshot-3.png"
          class="flex m-auto"
          alt="Print-ready road rally roadbook PDF in Rally Navigator"
        />
      </picture>
      <h2 class="font-semibold">Pre-Running and Route Checking</h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] font-bold"
      >
        <li>
          Once the Route is initially done. Its time to physically pre-run and
          check the Route and make any corrections prior to investing time
          drawing detailed Tulips.
        </li>
        <li>
          While checking route, capture a GPX Track of the Pre-Run to help with
          final Route editing
        </li>
        <li>
          While pre-running, check distance, headings, etc. at every Waypoint.
          Mark all Landmark and Terrain details. This can be done with a GPS,
          voice recorder or note pad.
        </li>
        <li>
          Record It Live (Pro): drive the route with a USB GPS and File &gt; GPS
          Roadbook Recorder builds the roadbook as you go. Re-drive it later with
          the GPS Roadbook Validator to check every waypoint.
          <router-link
            to="/pro-features#gps-recorder"
            class="text-[#FF0000] hover:underline"
            >See how it works</router-link
          >
        </li>
      </ul>
      <h2 class="font-semibold">Finalizing the Route After the Pre-Run</h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[14px] font-bold"
      >
        <li>Overlay GPX File of the Pre-Run</li>
        <li>
          Review the Overlay GPX Track and your route notes to make any
          necessary changes/corrections to Route.
        </li>
        <li>Add final detailed tulips, Icons and Waypoint Descriptions.</li>
        <li>You are Ready to Rock</li>
      </ul>
      <h2 class="font-semibold">Related Guides</h2>
      <ul
        class="list-disc pl-8 text-left py-2 flex flex-col gap-2 font-arial text-[16px] font-bold"
      >
        <li>
          <router-link
            to="/rally-roadbook"
            class="text-[#FF0000] hover:underline"
            >What is a rally roadbook? Creator &amp; software overview</router-link
          >
        </li>
        <li>
          <router-link to="/rally-maps" class="text-[#FF0000] hover:underline"
            >Rally route planning &amp; maps</router-link
          >
        </li>
        <li>
          <router-link
            to="/how-to-create-a-cross-country"
            class="text-[#FF0000] hover:underline"
            >How to create a cross-country roadbook</router-link
          >
        </li>
        <li>
          <router-link
            to="/time-speed-distance"
            class="text-[#FF0000] hover:underline"
            >Time Speed Distance (TSD) roadbooks</router-link
          >
        </li>
      </ul>
      <ADVRider />
    </div>
    <GuideDownloadCta />
  </section>
  <section class="my-8 mx-auto max-w-[1400px]">
    <FeaturesBanner />
    <PartnersSmall />
    <RallyLogoDownload />
    <div class="py-8 mt-8 border-t border-white/50">
      <p class="text-[17px] md:text-[20px]">
        <strong class="text-white">Running a road rally commercially?</strong> Pro Rally removes the "Not for Commercial Use" marking and adds time control zones: Regroup, Service, Flexi Service, Remote Service, Tyre Fitting, Parc Fermé, Technical or your own. A Regroup Out starts a new section and page, per FIA rules.
        <router-link to="/pro-features" class="text-[#FF0000] hover:underline"
          >See every Pro Rally feature &rarr;</router-link
        >
      </p>
    </div>
  </section>
  <RelatedGuides
    :links="[
        { to: '/how-to-create-a-cross-country', text: 'How to Create a Cross Country Roadbook' },
        { to: '/time-speed-distance', text: 'Time Speed Distance Rally' },
        { to: '/rally-tulip-diagrams', text: 'Rally Tulip Diagrams' },
        { to: '/fia-roadbook-specs-rules', text: 'FIA Road Rally Roadbook Specs' },
        { to: '/rally-roadbook', text: 'What Is a Rally Roadbook?' },
      ]"
  />
</template>

<script>
import YouTubeEmbed from './YouTubeEmbed.vue';
import FeaturesBanner from './FeaturesBanner.vue';
import RelatedGuides from './RelatedGuides.vue';
import GuideDownloadCta from './GuideDownloadCta.vue';
import SampleRoadbookIntro from './SampleRoadbookIntro.vue';
import { trackSampleDownload } from '../analytics';
import EventsBanner from './EventsBanner.vue';
import PartnersSmall from './PartnersSmall.vue';
import RallyLogoDownload from './RallyLogoDownload.vue';
import ADVRider from './ADVRider.vue';

export default {
  name: 'CreateRoadStage',
  components: {
    YouTubeEmbed,
    GuideDownloadCta,
    SampleRoadbookIntro,
    FeaturesBanner,
    RelatedGuides,
    RallyLogoDownload,
    PartnersSmall,
    EventsBanner,
    ADVRider,
  },
  data() {
    return {
      eventsBannerImages: [],
    };
  },
  mounted() {
    fetch('/BannerImagesLists/RoadRallyBannerImageList.json')
      .then((response) => response.json())
      .then((data) => {
        this.eventsBannerImages = data.sort(() => Math.random() - 0.5);
      });
  },
  methods: {
    onSampleClick(event) {
      trackSampleDownload(event);
    },
  },
};
</script>

<style scoped></style>
